/* eslint-disable class-methods-use-this */
import { IGtmDataLayer } from '@mono/api/lib/widgetApi/MonoWidget';
import { LicenseManager } from 'ag-grid-enterprise';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import jwt_decode from 'jwt-decode';
import applicationSettings from '../../Core/Settings/ApplicationSettings';
import applicationConstants from '../../Core/Utility/ApplicationConstants';
import { DemeterUserType } from '../../Generated/Raven-Demeter';
import store, { ReduxStore } from '../../Redux/ReduxStore';
import browserService from './BrowserService';
import loggingService from './Logging/LoggingService';
import trackingService, { TrackingService } from './Tracking/TrackingService';

// Service that will initialize the application with base settings to be able to bootstrap and start it.
class ApplicationInitializationService {
    private registerApiInterceptorsCalled = false;

    private stores: Record<string, ReduxStore> = { default: store };

    private accessToken: string = '';

    getStore = (storeId?: string): ReduxStore => this.stores[storeId ?? 'default'];

    getAccessToken = (): string => this.accessToken;

    setAccessToken = (accessToken: string): void => {
        this.accessToken = accessToken;
    };

    registerAll = (isMono: boolean, newStore: ReduxStore, storeId?: string, dataLayer?: IGtmDataLayer): void => {
        applicationSettings.isMono = isMono;

        this.registerReduxStore(newStore, storeId);
        this.registerAgGridLicense();
        this.registerApiInterceptors();
        if (dataLayer) {
            this.registerDataLayer(dataLayer);
        }
        this.registerDataDog();
    };

    registerDataDog = (): void => {
        TrackingService.initializeDatadog();
    };

    registerDataLayer = (dataLayer: IGtmDataLayer) => {
        loggingService.setDataLayer(dataLayer);
    };

    registerUserGuid = (accessToken: string) => {
        const decodedToken = jwt_decode(accessToken) as { cds_id: string; sub: string };

        loggingService.setUserGuid(decodedToken.cds_id);
        trackingService.setUserGuid(decodedToken.cds_id);
        trackingService.setUserEmail(decodedToken.sub);
    };

    unRegisterAll = (storeId: string): void => {
        const storeToRemove = this.stores[storeId];

        delete this.stores[storeId];
        if (storeToRemove === this.stores.default) {
            const existingStores = Object.values(this.stores);
            if (existingStores.length <= 1) {
                delete this.stores.default;
            } else {
                // eslint-disable-next-line prefer-destructuring
                this.stores.default = existingStores[0];
            }
        }
    };

    registerReduxStore = (newStore: ReduxStore, storeId?: string): void => {
        this.stores[storeId ?? 'default'] = newStore;

        // Always set the default store to the first one that gets registered.
        if (!this.stores.default || this.stores.default === store) {
            this.stores.default = newStore;
        }
    };

    registerAgGridLicense = (): void => {
        LicenseManager.setLicenseKey(
            // eslint-disable-next-line max-len
            'CompanyName=Stonex Group Inc.,LicensedGroup=technology-solutions-dev,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=10,LicensedProductionInstancesCount=0,AssetReference=AG-037161,SupportServicesEnd=16_March_2024_[v2]_MTcxMDU0NzIwMDAwMA==af16510f519246a8dfdae6a4fc0cf6ba',
        );
    };

    registerApiInterceptors = (): void => {
        if (this.registerApiInterceptorsCalled) {
            return;
        }

        axios.defaults.headers.common['Content-Type'] = 'application/json';
        axios.defaults.headers.common['Raven-Environment'] = applicationSettings.ravenEnvironment;
        axios.defaults.headers.common['Rift-Environment'] = applicationSettings.ravenEnvironment;
        axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';

        axios.interceptors.request.use(async (config: AxiosRequestConfig) => {
            const user = this.stores.default?.getState().user;
            const accessToken = user?.accessToken;
            const sessionToken = browserService.getLocalStorageItem(applicationConstants.SessionTokenHeader) ?? user?.sessionToken;
            const actingDemeterCompanyGuid = user?.actingDemeterCompanyGuid;

            if (accessToken) {
                config!.headers!.Authorization = `Bearer ${accessToken}`;
            }

            if (sessionToken) {
                config!.headers![applicationConstants.SessionTokenHeader] = sessionToken;
            }

            if (actingDemeterCompanyGuid && (user.userType === DemeterUserType.BusinessOwner || user?.userType === DemeterUserType.Administrator)) {
                config!.headers![applicationConstants.ActingDemeterCompanyHeader] = actingDemeterCompanyGuid;
            }

            return config;
        });

        axios.interceptors.request.use((config) => {
            const forceRefreshCache = localStorage.getItem('forceRefreshCache') === 'true';
            if (forceRefreshCache) {
                if (config.url) {
                    const [baseUrl, queryString] = config.url.split('?');
                    const params = new URLSearchParams(queryString || '');
                    params.set('refreshCache', 'true');
                    config.url = `${baseUrl}?${params.toString()}`;
                }
            }

            return config;
        });

        axios.interceptors.response.use(
            (response: AxiosResponse<any, any>) => response,
            (error: AxiosError) => {
                loggingService.trackException(error, `HTTP Error Response (${error.response?.status})`);
                if (error.response?.status === applicationConstants.HttpStatus.Unauthorized) {
                    if (error.response?.data?.detail === 'You already have an existing session.') {
                        const user = this.stores.default?.getState().user;
                        const sessionToken = user?.sessionToken;
                        // Only perform a logout if the user already has the session token (this avoids logout
                        // on initial login).
                        if (sessionToken && !applicationSettings.isMono) {
                            browserService.logout();
                        } else {
                            browserService.navigateToSessionExpiration();
                        }
                    } else if (error.response?.data?.detail === 'Your session has expired, you need to renew your session.') {
                        browserService.navigateToDashboard(true);
                    } else {
                        browserService.navigateToLoginPage();
                    }
                }

                return Promise.reject(error);
            },
        );

        this.registerApiInterceptorsCalled = true;
    };
}

const applicationInitializationService = new ApplicationInitializationService();

export default applicationInitializationService;
